import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { RoomType, ApiResponse } from '@/types'
import { propertyKeys } from './useProperties'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'

// Query keys
export const roomTypeKeys = {
  all: ['roomTypes'] as const,
  lists: () => [...roomTypeKeys.all, 'list'] as const,
  list: (propertyId: number) => [...roomTypeKeys.lists(), propertyId] as const,
  details: () => [...roomTypeKeys.all, 'detail'] as const,
  detail: (id: number) => [...roomTypeKeys.details(), id] as const,
}

// Fetch room types for a property
export function useRoomTypes(propertyId: number) {
  return useQuery({
    queryKey: roomTypeKeys.list(propertyId),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<RoomType[]>>(`/api/properties/${propertyId}/room_types`)
      return data.data
    },
    enabled: !!propertyId,
  })
}

// Fetch single room type
export function useRoomType(id: number) {
  return useQuery({
    queryKey: roomTypeKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<RoomType>>(`/api/room_types/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// Create room type
interface CreateRoomTypeInput {
  propertyId: number
  name: string
  floor?: string
}

export function useCreateRoomType() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ propertyId, ...input }: CreateRoomTypeInput) => {
      const { data } = await api.post<ApiResponse<RoomType>>(`/api/properties/${propertyId}/room_types`, {
        room_type: input,
      })
      return data.data
    },
    onSuccess: (_, variables) => {
      removeListQueries(queryClient, roomTypeKeys.list(variables.propertyId))
      invalidateDetailQueries(queryClient, propertyKeys.detail(variables.propertyId))
      // Also evict property units list (inline key to avoid circular import with useUnits)
      removeListQueries(queryClient, ['units', 'property-list'])
    },
  })
}

// Update room type
interface UpdateRoomTypeInput {
  id: number
  propertyId: number
  name?: string
  floor?: string
}

export function useUpdateRoomType() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, propertyId, ...input }: UpdateRoomTypeInput) => {
      const { data } = await api.patch<ApiResponse<RoomType>>(`/api/room_types/${id}`, {
        room_type: input,
      })
      return { data: data.data, propertyId }
    },
    onSuccess: (result) => {
      removeListQueries(queryClient, roomTypeKeys.list(result.propertyId))
      invalidateDetailQueries(queryClient, roomTypeKeys.detail(result.data.id))
      invalidateDetailQueries(queryClient, propertyKeys.detail(result.propertyId))
    },
  })
}

// Delete room type
export function useDeleteRoomType() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, propertyId }: { id: number; propertyId: number }) => {
      await api.delete(`/api/room_types/${id}`)
      return { id, propertyId }
    },
    onSuccess: (result) => {
      removeListQueries(queryClient, roomTypeKeys.list(result.propertyId))
      invalidateDetailQueries(queryClient, propertyKeys.detail(result.propertyId))
      removeListQueries(queryClient, ['units', 'property-list'])
    },
  })
}
